import React, { useState, useEffect } from "react";
import {
  Title,
  Text,
  Card,
  Loader,
  Center,
  Alert,
  Button,
  Group,
  Stack,
  Avatar,
  Badge,
  Divider,
  Tabs,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconRefresh,
  IconArrowLeft,
  IconUser,
  IconPlus,
  IconHeadset,
  IconUsers,
  IconSettings,
  IconCreditCard,
} from "@tabler/icons-react";
import { useParams, useNavigate } from "react-router-dom";
import { getAdminApiEndpoint, setAdminCompanyCode } from "../../config/config";
import { adminHeaders } from "../../services/adminApi";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import PageContent from "../shared/PageContent/PageContent";
import AddAdminModal from "./AddAdminModal";
import AdminCompanyUsersPage from "./AdminCompanyUsersPage";
import { handleGenericError } from "../../utils/errorHandler";

const ClientDetailsPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addAdminModalOpened, setAddAdminModalOpened] = useState(false);
  const { user, getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();
  const [activeTab, setActiveTab] = useState("admins");

  useEffect(() => {
    if (code) {
      // Keep the admin tools that aren't opened from a company page pointed at
      // the company being worked on.
      setAdminCompanyCode(code);
      fetchClientDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getAdminApiEndpoint(`/v2/admin/client/${code}`),
        {
          method: "GET",
          headers: adminHeaders(jwt, code),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch client details: ${response.status}`);
      }

      const result = await response.json();
      setClient(result.data || result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate("/clients");
  };

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const handleAddAdmin = async adminData => {
    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getAdminApiEndpoint(`/v2/admin/company-admin`),
        {
          method: "POST",
          headers: adminHeaders(jwt, code),
          body: JSON.stringify(adminData),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to add administrator: ${response.status}`);
      }

      showSuccess("Administrator added successfully");
      fetchClientDetails(); // Refresh the client details to show the new admin
    } catch (err) {
      handleGenericError(err, "Failed to add administrator");
      throw err;
    }
  };

  const handleCustomerSupportClick = () => {
    navigate(`/customer-support/${code}`);
  };

  const handleManageSubscriptionClick = () => {
    navigate(`/client/${code}/subscription`);
  };

  // Handle tab change
  const handleTabChange = value => {
    setActiveTab(value);
  };

  if (loading) {
    return (
      <PageContent
        title="Client Details"
        subtitle="Loading client information..."
      >
        <Center style={{ height: 400 }}>
          <Stack align="center">
            <Loader size="lg" />
            <Text>Loading client details...</Text>
          </Stack>
        </Center>
      </PageContent>
    );
  }

  if (error) {
    return (
      <PageContent
        title="Client Details"
        subtitle="Error loading client information"
      >
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error loading client details"
          color="red"
          withCloseButton={false}
        >
          <Text mb="md">{error}</Text>
          <Group gap="sm">
            <Button
              leftSection={<IconRefresh size={14} />}
              onClick={fetchClientDetails}
              color="red"
              variant="outline"
            >
              Retry
            </Button>
            <Button
              leftSection={<IconArrowLeft size={14} />}
              onClick={handleBackClick}
              variant="outline"
            >
              Back to Clients
            </Button>
          </Group>
        </Alert>
      </PageContent>
    );
  }

  return (
    <PageContent
      title={client?.name || "Client Details"}
      subtitle="Manage client administrators and settings"
    >
      <Group justify="space-between" mb="lg">
        <Button
          leftSection={<IconArrowLeft size={16} />}
          onClick={handleBackClick}
          variant="outline"
        >
          Back to Clients
        </Button>

        {/* Only show Customer Support and Manage Subscription buttons for REPPI_ADMIN users */}
        {user?.role === "REPPI_ADMIN" && (
          <Group gap="sm">
            <Button
              leftSection={<IconHeadset size={16} />}
              onClick={handleCustomerSupportClick}
              variant="filled"
              color="blue"
            >
              Customer Support
            </Button>
            <Button
              leftSection={<IconCreditCard size={16} />}
              onClick={handleManageSubscriptionClick}
              variant="filled"
              color="green"
            >
              Manage Subscription
            </Button>
          </Group>
        )}
      </Group>

      <Stack gap="lg">
        {/* Client Information Card */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={2} mb="md">
            Client Information
          </Title>
          <Group gap="xs" mb="sm">
            <Text fw={500} color="dimmed" style={{ minWidth: 100 }}>
              Name:
            </Text>
            <Text>{client?.name || "N/A"}</Text>
          </Group>
          <Group gap="xs" mb="sm">
            <Text fw={500} color="dimmed" style={{ minWidth: 100 }}>
              ID:
            </Text>
            <Text>{client?.id || "N/A"}</Text>
          </Group>
          {client?.created_at && (
            <Group gap="xs" mb="sm">
              <Text fw={500} color="dimmed" style={{ minWidth: 100 }}>
                Created:
              </Text>
              <Text>{formatDate(client.created_at)}</Text>
            </Group>
          )}
        </Card>

        {/* Tabs for Client Management */}
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List>
            <Tabs.Tab value="admins" leftSection={<IconSettings size={14} />}>
              Client Administrators
            </Tabs.Tab>
            {user?.role === "REPPI_ADMIN" && (
              <Tabs.Tab
                value="company-users"
                leftSection={<IconUsers size={14} />}
              >
                Company Users
              </Tabs.Tab>
            )}
          </Tabs.List>

          <Tabs.Panel value="admins" pt="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Title order={3}>Client Administrators</Title>
                <Group gap="sm">
                  <Badge color="blue" variant="filled">
                    {client?.company_admins?.length || 0} Admin
                    {client?.company_admins?.length !== 1 ? "s" : ""}
                  </Badge>
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={() => setAddAdminModalOpened(true)}
                    size="sm"
                  >
                    Add Admin
                  </Button>
                </Group>
              </Group>

              {!client?.company_admins || client.company_admins.length === 0 ? (
                <Center style={{ height: 200 }}>
                  <Stack align="center">
                    <IconUser size={48} color="gray" />
                    <Text size="lg" color="dimmed">
                      No administrators found for this client.
                    </Text>
                  </Stack>
                </Center>
              ) : (
                <Stack gap="md">
                  {client.company_admins.map((admin, index) => (
                    <div key={admin.id}>
                      <Group gap="md">
                        <Avatar
                          src={admin.user?.profile_image_url}
                          size="lg"
                          radius="xl"
                        >
                          {getInitials(
                            admin.user?.first_name,
                            admin.user?.last_name
                          )}
                        </Avatar>

                        <Stack gap="xs" style={{ flex: 1 }}>
                          <Group gap="sm">
                            <Text fw={600} size="md">
                              {admin.user?.first_name} {admin.user?.last_name}
                            </Text>
                            {admin.user?.rep_id && (
                              <Badge color="green" variant="light" size="sm">
                                Rep ID: {admin.user.rep_id}
                              </Badge>
                            )}
                          </Group>

                          <Text size="sm" color="dimmed">
                            {admin.user?.email}
                          </Text>

                          <Group gap="sm">
                            <Text size="xs" color="dimmed">
                              Admin ID: {admin.id}
                            </Text>
                            <Text size="xs" color="dimmed">
                              User ID: {admin.user_id}
                            </Text>
                            {admin.created_at && (
                              <Text size="xs" color="dimmed">
                                Added: {formatDate(admin.created_at)}
                              </Text>
                            )}
                          </Group>
                        </Stack>
                      </Group>

                      {index < client.company_admins.length - 1 && (
                        <Divider mt="md" />
                      )}
                    </div>
                  ))}
                </Stack>
              )}
            </Card>
          </Tabs.Panel>

          {user?.role === "REPPI_ADMIN" && (
            <Tabs.Panel value="company-users" pt="md">
              {/* Use the existing AdminCompanyUsersPage component but render its content within our tab */}
              <div style={{ marginTop: "-20px" }}>
                <AdminCompanyUsersPage companyCode={code} />
              </div>
            </Tabs.Panel>
          )}
        </Tabs>
      </Stack>

      <AddAdminModal
        opened={addAdminModalOpened}
        onClose={() => setAddAdminModalOpened(false)}
        onSave={handleAddAdmin}
      />
    </PageContent>
  );
};

export default ClientDetailsPage;
